import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import styles from './VariantsStep1.module.css';

const MAX_ATTRIBUTES = 3;
const MAX_TAGS_PER_ATTRIBUTE = 5;
const DEFAULT_ATTRIBUTES = ['', ''];

const PlusDropdownIndicator = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 8px', flexShrink: 0 }}>
    <path d="M8 0.299805C8.71786 0.299805 9.29959 0.881823 9.2998 1.59961V6.7002H14.4004C15.1182 6.70041 15.7002 7.28213 15.7002 8C15.7002 8.71786 15.1182 9.29959 14.4004 9.2998H9.2998V14.4004C9.29959 15.1182 8.71786 15.7002 8 15.7002C7.28214 15.7002 6.70041 15.1182 6.7002 14.4004V9.2998H1.59961C0.881823 9.29959 0.299805 8.71786 0.299805 8C0.299805 7.28213 0.881821 6.70041 1.59961 6.7002H6.7002V1.59961C6.70041 0.881822 7.28214 0.299805 8 0.299805Z" fill="url(#vs1_grad)" stroke="#F5FAFF" strokeWidth="0.6" />
    <defs>
      <linearGradient id="vs1_grad" x1="-5.96046e-08" y1="8" x2="14.6857" y2="16" gradientUnits="userSpaceOnUse">
        <stop stopColor="#2766AA" />
        <stop offset="1" stopColor="#0752B0" />
      </linearGradient>
    </defs>
  </svg>
);


/**
 * Step 1 of the Variants tab: select variation theme from filtered enums (or manual attributes),
 * then define variant values per attribute. '/' in enum names separates attributes.
 */
const VariantsStep1 = ({
  onContinue,
  onNoVariants,
  initialAttributes,
  initialVariantValues,
  isLoading = false,
  filteredEnums = [],
  attributeEnums = {},
}) => {
  const hasFilteredEnums = Array.isArray(filteredEnums) && filteredEnums.length > 0;

  const getInitialSelectedEnum = () => {
    if (hasFilteredEnums && Array.isArray(initialAttributes) && initialAttributes.length > 0) {
      const joined = initialAttributes.join('/');
      return filteredEnums.includes(joined) ? joined : '';
    }
    return '';
  };

  const [selectedEnum, setSelectedEnum] = useState(getInitialSelectedEnum);

  const getInitialAttributes = () => {
    const sel = getInitialSelectedEnum();
    if (hasFilteredEnums && sel) {
      return sel.split('/').map((s) => s.trim()).filter(Boolean);
    }
    if (Array.isArray(initialAttributes) && initialAttributes.length > 0) {
      return [...initialAttributes];
    }
    return [...DEFAULT_ATTRIBUTES];
  };

  const [attributes, setAttributes] = useState(getInitialAttributes);

  const getInitialVariantValues = () => {
    const initialAttrs = getInitialAttributes();
    if (Array.isArray(initialVariantValues) && initialVariantValues.length >= initialAttrs.length) {
      return initialAttrs.map((_, i) =>
        Array.isArray(initialVariantValues[i]) ? [...initialVariantValues[i]] : []
      );
    }
    return initialAttrs.map(() => []);
  };

  const [variantValues, setVariantValues] = useState(getInitialVariantValues);
  const isInitialMount = useRef(true);

  // When user changes selectedEnum, update attributes and reset variant values
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (hasFilteredEnums && selectedEnum) {
      const attrs = selectedEnum.split('/').map((s) => s.trim()).filter(Boolean);
      setAttributes(attrs);
      setVariantValues(attrs.map(() => []));
    }
  }, [selectedEnum, hasFilteredEnums]);

  const handleEnumSelect = (e) => {
    const value = e.target.value || '';
    setSelectedEnum(value);
  };

  const updateAttribute = (index, value) => {
    setAttributes((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const addAttribute = () => {
    if (attributes.length >= MAX_ATTRIBUTES) return;
    setAttributes((prev) => [...prev, '']);
    setVariantValues((prev) => [...prev, []]);
  };

  const addVariantValue = (attrIndex, value) => {
    // Split comma-separated input into separate entries (single values pass through unchanged).
    const tokens = String(value || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (tokens.length === 0) return;
    setVariantValues((prev) => {
      const current = prev[attrIndex] || [];
      const room = MAX_TAGS_PER_ATTRIBUTE - current.length;
      if (room <= 0) return prev;
      const additions = tokens.slice(0, room);
      return prev.map((arr, i) => (i === attrIndex ? [...arr, ...additions] : arr));
    });
  };

  const removeVariantValue = (attrIndex, valueIndex) => {
    setVariantValues((prev) =>
      prev.map((arr, i) =>
        i === attrIndex ? arr.filter((_, j) => j !== valueIndex) : arr
      )
    );
  };

  const handleTagInputKeyDown = (attrIndex, e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.target;
      const value = input.value.trim();
      const count = (variantValues[attrIndex] || []).length;
      if (value && count < MAX_TAGS_PER_ATTRIBUTE) {
        addVariantValue(attrIndex, value);
        input.value = '';
      }
    }
  };

  const handleTagInputBlur = (attrIndex, e) => {
    const input = e.target;
    const value = input.value.trim();
    const count = (variantValues[attrIndex] || []).length;
    if (value && count < MAX_TAGS_PER_ATTRIBUTE) {
      addVariantValue(attrIndex, value);
      input.value = '';
    }
  };

  const isTagLimitReached = (attrIndex) => (variantValues[attrIndex] || []).length >= MAX_TAGS_PER_ATTRIBUTE;

  const handleCreateCombinations = () => {
    const names = attributes.map((a) => (a || '').trim()).filter(Boolean);
    if (names.length === 0) return;
    const values = variantValues.slice(0, names.length);
    onContinue(names, values);
  };

  const canCreate = attributes.some((a) => (a || '').trim().length > 0);

  // Return constrained enum values for an attribute name, or null if free-text.
  const getAllowedValues = (attrName) => {
    if (!attrName || !attributeEnums) return null;
    const key = attrName.trim().toLowerCase().replace(/\s+/g, '_');
    return attributeEnums[key] ?? null;
  };

  const getSectionTitle = (attrIndex) => {
    const name = (attributes[attrIndex] || '').trim();
    return name ? `Add ${name} Variants` : `Add Attribute ${attrIndex + 1} Variants`;
  };

  const getPlaceholder = (attrIndex) => {
    if (isTagLimitReached(attrIndex)) return 'Maximum 5 variants';
    const name = (attributes[attrIndex] || '').trim();
    return name ? `Enter ${name.toLowerCase()} variants here` : `Enter attribute ${attrIndex + 1} variants here`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.contentRow}>
        <div className={styles.attributesSection}>
          <div className={styles.attributesGroup}>
            {hasFilteredEnums ? (
              <div className={styles.attributeBlock}>
                <div className={styles.attributeLabel}>Variation Theme</div>
                <div className={styles.enumSelectWrapper}>
                  <select
                    className={styles.enumSelect}
                    value={selectedEnum}
                    onChange={handleEnumSelect}
                    disabled={isLoading}
                    data-testid="variation-theme-select"
                  >
                    <option value="">Select variation theme</option>
                    {filteredEnums.filter((enumVal) => enumVal.split('/').filter(Boolean).length <= 3).map((enumVal) => (
                      <option key={enumVal} value={enumVal}>
                        {enumVal}
                      </option>
                    ))}
                  </select>
                  <span className={styles.enumSelectIcon} aria-hidden="true">
                    <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.7625 0L7.5 5.56275L13.2375 0L15 1.71255L7.5 9L0 1.71255L1.7625 0Z" fill="#737373"/>
                    </svg>
                  </span>
                </div>
              </div>
            ) : (
              <>
                {attributes.map((value, index) => (
                  <div key={index} className={styles.attributeBlock}>
                    <div className={styles.attributeLabel}>Attribute {index + 1}</div>
                    <input
                      type="text"
                      className={styles.attributeInput}
                      value={value}
                      onChange={(e) => updateAttribute(index, e.target.value)}
                      placeholder={`Attribute ${index + 1}`}
                      disabled={isLoading}
                    />
                  </div>
                ))}
                {attributes.length < MAX_ATTRIBUTES && (
                  <button
                    type="button"
                    className={styles.addButton}
                    onClick={addAttribute}
                    disabled={isLoading}
                    aria-label="Add attribute"
                  >
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.5 1V12M1 6.5H12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
          <button
            type="button"
            className={styles.noVariantsLink}
            onClick={onNoVariants}
            disabled={isLoading}
          >
            Product Has no Variants
          </button>
        </div>
      </div>

      {/* Variant value fields: one per attribute (only show when enum selected or in manual mode) */}
      {(hasFilteredEnums ? selectedEnum : true) && (
        <div className={styles.variantValuesSection}>
          {attributes.map((attrName, attrIndex) => {
            const allowed = getAllowedValues(attrName);
            const tags = variantValues[attrIndex] || [];
            const limitReached = isTagLimitReached(attrIndex);
            const tagSet = new Set(tags);
            return (
              <div key={attrIndex} className={styles.variantValueBlock}>
                <div className={styles.variantValueHeader}>
                  <span className={styles.variantValueTitle}>{getSectionTitle(attrIndex)}</span>
                  <span className={styles.variantValueHint}>
                    {allowed
                      ? 'Select from the options below'
                      : 'Type Value and press Enter or click outside'}
                  </span>
                </div>
                <div className={styles.tagInputWrapper}>
                  <div className={styles.tagsAndInput}>
                    {tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className={styles.tag}>
                        <span className={styles.tagLabel}>{tag}</span>
                        <button
                          type="button"
                          className={styles.tagRemove}
                          onClick={() => removeVariantValue(attrIndex, tagIndex)}
                          disabled={isLoading}
                          aria-label={`Remove ${tag}`}
                        >
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L7 7M7 1L1 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </button>
                      </span>
                    ))}
                    {allowed ? (
                      <Select
                        options={allowed
                          .filter((v) => !tagSet.has(String(v)))
                          .map((v) => ({ value: v, label: String(v) }))}
                        onChange={(opt) => {
                          if (opt) addVariantValue(attrIndex, String(opt.value));
                        }}
                        value={null}
                        isDisabled={isLoading || limitReached}
                        isSearchable
                        placeholder={limitReached ? 'Maximum 5 variants' : `Select ${(attrName || '').toLowerCase()}…`}
                        classNamePrefix="react-select"
                        components={{
                          DropdownIndicator: PlusDropdownIndicator,
                          IndicatorSeparator: () => null,
                        }}
                        styles={{
                          container: (p) => ({ ...p, flex: 1, minWidth: 300 }),
                          control: (p) => ({
                            ...p,
                            border: 'none',
                            boxShadow: 'none',
                            background: 'transparent',
                            minHeight: 32,
                            fontSize: 14,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                          }),
                          menu: (p) => ({ ...p, zIndex: 1000, fontSize: 14 }),
                          placeholder: (p) => ({ ...p, color: '#898989' }),
                          singleValue: (p) => ({ ...p, fontSize: 14 }),
                        }}
                      />
                    ) : (
                      <input
                        type="text"
                        className={styles.tagInput}
                        placeholder={getPlaceholder(attrIndex)}
                        onKeyDown={(e) => handleTagInputKeyDown(attrIndex, e)}
                        onBlur={(e) => handleTagInputBlur(attrIndex, e)}
                        disabled={isLoading || limitReached}
                        aria-label={limitReached ? 'Maximum 5 variants reached' : getPlaceholder(attrIndex)}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        className={styles.createButton}
        onClick={handleCreateCombinations}
        disabled={isLoading || !canCreate}
      >
        <span className={styles.createButtonText}>Create Combinations</span>
      </button>
    </div>
  );
};

export default VariantsStep1;
